import styled from '@emotion/styled';

import type { RisingStarBonusHistory } from 'services/hooks/risingStarBonus';

import Flex from 'components/util/Flex';

import { useRisingStarCopy } from './copy';
import { formatAmount } from './format';
import { STitle } from './style';
import AmountValue from '../shared/AmountValue';
import { SInformationItemWrapper } from '../shared/style';

type Props = {
  history: RisingStarBonusHistory[];
  isBlur: boolean;
  /** 카드 제목이 이미 '적립 내역'인 곳(별지기 화면)에서는 소제목을 뺀다 */
  showTitle?: boolean;
};

/** 보너스 적립 내역 (정책 B-1) — 월별 기본 리워드 + 달성 보너스(− 회수) = 월 합계. 값은 응답 그대로 */
export default function BonusHistory({ history, isBlur, showTitle = true }: Props) {
  const COPY = useRisingStarCopy();
  if (history.length === 0) return null;

  return (
    // 월 사이 간격은 rowGap 으로 — margin 으로 두면 소제목이 없는 별지기 카드에서 첫 월 위 여백이 두 번 들어간다
    <Flex direction="column" rowGap={12}>
      {showTitle && <STitle>{COPY.historyTitle}</STitle>}
      {history.map((h) => (
        <Flex key={h.targetMonth} direction="column">
          <SMonth>
            {COPY.historyMonth(h.targetMonth)} · {COPY.historyPaidAt(h.payoutDate)}
          </SMonth>
          <SInformationItemWrapper>
            <span>{COPY.baseReward}</span>
            <AmountValue amountFormat={formatAmount(h.basicReward)} isBlur={isBlur} />
          </SInformationItemWrapper>
          <SInformationItemWrapper>
            <span>
              {COPY.bonus} ({h.tierThreshold === null ? COPY.notAchieved : COPY.tierRange(h.tierThreshold)})
            </span>
            <AmountValue amountFormat={formatAmount(h.bonus)} isBlur={isBlur} />
          </SInformationItemWrapper>
          {/* 회수 없는 달은 0 으로 온다 — 누락·null 도 회수 없음으로 본다 */}
          {!!h.clawback && (
            <SInformationItemWrapper>
              <span>{COPY.recovered}</span>
              <AmountValue amountFormat={formatAmount(h.clawback)} isBlur={isBlur} />
            </SInformationItemWrapper>
          )}
          <SInformationItemWrapper $invisibleBorderBottom>
            <span>{COPY.monthTotal}</span>
            <AmountValue amountFormat={formatAmount(h.monthTotal)} isBlur={isBlur} />
          </SInformationItemWrapper>
        </Flex>
      ))}
    </Flex>
  );
}

const SMonth = styled.span`
  color: ${({ theme }) => theme.color.label.secondary};
  ${({ theme }) => theme.font.caption1_sb};
`;
